import {valueFromJSON} from '@/helpers/string.js'
import {get, set} from '@/helpers/object.js'
import {DateTime} from 'luxon'

/**
 * Base model
 */
export class BaseModel {

  //JSON key map (to convert incoming JSON keys to model keys)
  static jsonKeyMap = {}

  //JSON parsers (to convert incoming JSON values to model values)
  static jsonParsers = {}

  //Sub models
  static subModels = {}

  /**
   * Constructor
   */
  constructor() {

    //Model flag
    Object.defineProperty(this, 'isModel', {
      enumerable: false,
      get() {
        return true
      },
    })

    //JSON
    Object.defineProperty(this, 'json', {
      enumerable: false,
      writable: true,
    })
  }

  /**
   * Get value of property at given path
   */
  get(path, defaultValue) {
    return get(this, path, defaultValue)
  }

  /**
   * Set value of property at given path
   */
  set(path, value) {
    return set(this, path, value)
  }

  /**
   * Clone model
   */
  clone(data = {}) {

    //Create new instance of this model
    const clone = new this.constructor()

    //Assign properties
    //NOTE: Not doing this via JSON conversion, because we may not be sending all
    //data to the server as JSON, and the structure might mismatch. So just doing
    //a manual copy here, which will mostly try to be reasonable smart in handling
    //objects, submodels and arrays etc.
    for (const key in this) {
      clone[key] = this.copyProperty(this, key)
    }

    //Set any passed in data now with a shallow assignment
    if (data && Object.keys(data).length) {
      Object.assign(clone, data)
    }

    //Return the clone
    return clone
  }

  /**
   * Helper to copy a property of an object (for cloning)
   */
  copyProperty(obj, key) {
    if (obj[key] instanceof DateTime) {
      return DateTime.fromObject(obj[key].toObject())
    }
    if (obj[key] instanceof BaseModel) {
      return obj[key].clone()
    }
    if (Array.isArray(obj[key])) {
      return obj[key].map((value, index) => this.copyProperty(obj[key], index))
    }
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      const clone = {}
      for (const subKey in obj[key]) {
        clone[subKey] = this.copyProperty(obj[key], subKey)
      }
      return clone
    }
    return obj[key]
  }

  /**************************************************************************
   * To JSON conversion
   ***/

  /**
   * Default to JSON conversion simply relies on the received JSON object,
   * e.g. treating models as immutable. This method can be extended in other
   * child classes to provide more advanced conversion.
   */
  toJson(data = {}) {
    const {json} = this
    return Object.assign({}, json, data)
  }

  /**************************************************************************
   * From JSON conversion
   ***/

  /**
   * Initialize model from JSON
   */
  fromJson(json) {

    //Store JSON
    this.json = json

    //Process JSON
    if (json && typeof json === 'object') {
      for (const key in json) {
        this.setJsonValue(key, json[key], json)
      }
    }

    //Ensure all submodels are created (not all keys may be present in JSON)
    for (const key in this.constructor.subModels) {
      if (typeof this[key] === 'undefined') {
        this[key] = this.constructor.createSubModel(this.constructor.subModels[key])
      }
    }
  }

  /**
   * Set JSON value of a given key
   */
  setJsonValue(key, value, json) {

    //Convert key
    if (this.constructor.jsonKeyMap[key]) {
      key = this.constructor.jsonKeyMap[key]
    }

    //Run through mapping function
    if (this.constructor.jsonParsers[key]) {
      value = this.constructor.jsonParsers[key](value, json)
    }

    //Create sub model
    if (this.constructor.subModels[key]) {
      value = this.constructor.createSubModel(
        this.constructor.subModels[key], value
      )
    }

    //Set value
    this[key] = valueFromJSON(value)
  }

  /**************************************************************************
   * Static helpers
   ***/

  /**
   * Create a sub model
   */
  static createSubModel(model, value) {

    //Check if array given
    const isArray = Array.isArray(model)
    const Model = isArray ? model[0] : model

    //If undefined, check what we were expecting
    if (typeof value === 'undefined') {
      return isArray ? [] : null
    }

    //Expecting an array but no array given
    if (isArray && !Array.isArray(value)) {
      value = value ? [value] : []
    }

    //If no model specified or null, return as is
    if (!Model || value === null) {
      return value
    }

    //Convert to model
    return Model.createFrom(value)
  }

  /**
   * Create models from given JSON data
   */
  static createFrom(json = {}) {

    //Array of data
    if (Array.isArray(json)) {
      return json.map(item => this.createFrom(item))
    }

    //Ensure plain object given
    if (json instanceof BaseModel) {
      throw new Error(`Cannot use createFrom() to create a model from another model. Use model.clone() instead.`)
    }

    //Single model
    const model = new this()
    model.fromJson(json)
    return model
  }
}
